import { useEffect, useRef, useState } from 'react';
import './otp.css';
import lockShieldImg from '../assets/icon.png';

const CODE_LENGTH = 6;
const RESEND_SECONDS = 45;

function ChevronLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="8.5" cy="10.5" r="1" fill="currentColor" />
      <circle cx="12" cy="10.5" r="1" fill="currentColor" />
      <circle cx="15.5" cy="10.5" r="1" fill="currentColor" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockSmallIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="11" width="14" height="10" rx="2.5" stroke="#7457e8" strokeWidth="1.8" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="#7457e8" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function formatTimer(seconds) {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

export default function Otp({ contact = '+91 98765 43210', method = 'phone', onBack, onEdit, onVerified }) {
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const code = digits.join('');
  const isComplete = code.length === CODE_LENGTH;

  function setDigitAt(index, value) {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function handleChange(index, e) {
    const value = e.target.value.replace(/\D/g, '');
    if (!value) {
      setDigitAt(index, '');
      return;
    }
    const chars = value.split('');
    chars.forEach((ch, offset) => {
      const target = index + offset;
      if (target < CODE_LENGTH) setDigitAt(target, ch);
    });
    const nextIndex = Math.min(index + chars.length, CODE_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setDigitAt(index - 1, '');
    }
  }

  function handleResend() {
    if (secondsLeft > 0) return;
    setSecondsLeft(RESEND_SECONDS);
    setDigits(Array(CODE_LENGTH).fill(''));
    inputRefs.current[0]?.focus();
  }

  return (
    <div className="otp-screen">
      <div className="otp-frame">
        <div className="otp-header">
          <button type="button" className="otp-back-btn" onClick={onBack} aria-label="Go back">
            <ChevronLeftIcon />
          </button>

          <div className="otp-glow" />
          <div className="otp-icon-wrap">
            <img src={lockShieldImg} alt="" className="otp-icon-img" />
          </div>
          <h1 className="otp-title">
            {method === 'email' ? 'Verify your email' : 'Verify your number'}
          </h1>
          <p className="otp-subtitle">Enter the 6-digit code sent to</p>
          <div className="otp-contact-row">
            <span className="otp-contact">{contact}</span>
            <button type="button" className="otp-edit-btn" onClick={onEdit} aria-label="Edit">
              <PencilIcon />
            </button>
          </div>
        </div>

        <form
          className="otp-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (isComplete) onVerified?.(code);
          }}
        >
          <div className="otp-msg-icon">
            <MessageIcon />
          </div>
          <h2 className="otp-card-title">Enter OTP</h2>
          <p className="otp-card-subtitle">
            We&apos;ve sent a 6-digit verification code to your{' '}
            {method === 'email' ? 'email address' : 'phone number'}
          </p>

          <div className="otp-boxes">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={CODE_LENGTH}
                placeholder="-"
                className={`otp-box ${digit ? 'filled' : ''}`}
                value={digit}
                onChange={(e) => handleChange(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
              />
            ))}
          </div>

          <p className="otp-resend-row">
            Didn&apos;t receive the code?
            <button
              type="button"
              className="otp-resend-link"
              onClick={handleResend}
              disabled={secondsLeft > 0}
            >
              Resend OTP{secondsLeft > 0 ? ` (${formatTimer(secondsLeft)})` : ''}
            </button>
          </p>

          <button type="submit" className="otp-submit" disabled={!isComplete}>
            Verify &amp; Continue <ArrowRightIcon />
          </button>

          <p className="otp-secure-note">
            <LockSmallIcon />
            Your information is secure and encrypted
          </p>
        </form>
      </div>
    </div>
  );
}
